import { runRenderPipeline } from 'modules/manager/domain/render';
import mediaManagerRepository, { StageRenderedImageInput } from 'modules/manager/domain/repositories/media-manager-repository';
import { selectActiveTemplate, selectError } from 'modules/manager/store';
import { useGetStandingsRenderContextQuery } from 'modules/manager/store/standings-render-api';
import { TemplateType } from 'modules/manager/domain/template-type';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import {
  HeadlessRenderCanvas,
  RenderErrorBoundary,
  StatusOverlay,
  collectBgUrls,
  loadCanvasJson,
  makeStatusTitle,
  nextFrame,
  parseId,
  toUserMessage,
} from './render-shared';
import { useRenderPageState } from './use-render-page-state';

const statusTitle = makeStatusTitle({
  loading: 'Loading template and stage data…',
  rendering: 'Rendering standings…',
  done: 'Images saved successfully.',
});

const StandingsRenderPageInner: React.FC = () => {
  const [params] = useSearchParams();

  const stageId = parseId(params.get('stageId'));
  const templateId = parseId(params.get('templateId'));
  const bgUrls = useMemo(() => collectBgUrls(params), [params]);

  const activeTemplate = useAppSelector(selectActiveTemplate);
  const loadError = useAppSelector(selectError);
  const ctxQuery = useGetStandingsRenderContextQuery(stageId !== null ? { stageId } : skipToken);

  const { editor, setEditor, status, setStatus, errorMessage, setErrorMessage, links, setLinks, orchestrated } = useRenderPageState({
    templateId,
    missingParamMessage: stageId === null ? 'Missing stageId in URL' : null,
    contextError: ctxQuery.error,
    contextErrorFallback: 'Failed to load standings render context',
  });

  useEffect(() => {
    if (orchestrated.current) return;
    if (!editor || !activeTemplate || !ctxQuery.data || stageId === null) return;
    if (loadError) {
      console.error(loadError);
      setStatus('error');
      setErrorMessage(loadError);
      return;
    }
    if (activeTemplate.templateType !== TemplateType.TOURNAMENT_TABLE) {
      setStatus('error');
      setErrorMessage('Template is not a Турнирная таблица (TOURNAMENT_TABLE) template');
      return;
    }
    orchestrated.current = true;
    setStatus('rendering');

    (async () => {
      try {
        const images: StageRenderedImageInput[] = [];
        for (const target of ctxQuery.data!.targets) {
          await loadCanvasJson(editor, activeTemplate.version, activeTemplate.objects ?? []);
          await runRenderPipeline(editor, target, {
            bgUrls,
            templateType: activeTemplate.templateType,
            layout: { settings: activeTemplate.matchLayout, authoredObjects: activeTemplate.objects ?? [] },
          });
          await nextFrame();
          const dataUrl = editor.canvas.toDataURL({ format: 'png', multiplier: 1 });
          images.push({
            groupKey: target.group ? String(target.group.id) : '',
            groupId: target.group?.id,
            groupName: target.group?.name,
            dataUrl,
          });
        }
        const saved = await mediaManagerRepository.persistStageRenderedImages({
          stageId,
          templateId: activeTemplate.id,
          images,
        });
        setLinks(
          saved.map((row) => ({
            url: row.imageUrl,
            label: row.groupName ?? (row.groupKey ? `Group ${row.groupKey}` : 'Standings table'),
          })),
        );
        setStatus('done');
      } catch (err) {
        console.error(err);
        setStatus('error');
        setErrorMessage(toUserMessage(err));
      }
    })();
  }, [editor, activeTemplate, ctxQuery.data, loadError, stageId, bgUrls, orchestrated, setStatus, setErrorMessage, setLinks]);

  if (!activeTemplate) {
    return <StatusOverlay status={status} message={errorMessage ?? 'Loading template…'} title={statusTitle} />;
  }

  return (
    <>
      <HeadlessRenderCanvas template={activeTemplate} editor={editor} onReady={setEditor} />
      <StatusOverlay status={status} message={errorMessage} title={statusTitle} links={links} />
    </>
  );
};

const StandingsRenderPage: React.FC = () => (
  <RenderErrorBoundary>
    <StandingsRenderPageInner />
  </RenderErrorBoundary>
);

export default StandingsRenderPage;
